import { useState, useEffect } from 'react';
import { Clock, Calendar, Plus, Edit2, Trash2, Save, X, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import toast from 'react-hot-toast';

export default function TradesmanDashboard() {
  const API_URL = process.env.REACT_APP_API_URL;
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const firstName = storedUser?.firstName || "User";
  const token = storedUser?.token;
  const authHeaders = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const [isShoppingListDirty, setIsShoppingListDirty] = useState(false);
  const [shoppingList, setShoppingList] = useState("");
  const [workCategories, setWorkCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [category, setCategory] = useState("");
  const [desc, setDesc] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [entryMessage, setEntryMessage] = useState({ type: "", text: "" });
  const [entriesMessage, setEntriesMessage] = useState({ type: "", text: "" });
  const [shoppingMessage, setShoppingMessage] = useState({ type: "", text: "" });
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [lastUsedTimes, setLastUsedTimes] = useState(() => {
    const saved = localStorage.getItem("lastWorkEntryTimes");
    return saved ? JSON.parse(saved) : null;
  });


  function Message({ type, text }) {
    if (!text) return null;

    return (
      <div className={`p-2 mb-3 rounded-lg text-sm font-semibold
      ${type === "error" ? "bg-red-100 text-red-700 border border-red-300"
          : "bg-green-100 text-green-700 border border-green-300"}`}>
        {text}
      </div>
    );
  }

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = currentDate.toLocaleString("default", { month: "long" });
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const startIndex = (firstDayOfMonth + 6) % 7;
  const daysArray = Array(startIndex).fill(null).concat([...Array(daysInMonth)].map((_, i) => i + 1));

  const goToPrevMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() - 1);
    setCurrentDate(newDate);
  };
  const goToNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatSelectedDate = () =>
    selectedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const [entries, setEntries] = useState([]);

  // FIX PROBLÈME 3: Fonction pour vérifier les chevauchements
  const checkOverlap = (newStart, newEnd, excludeId = null) => {
    const [newStartH, newStartM] = newStart.split(":").map(Number);
    const [newEndH, newEndM] = newEnd.split(":").map(Number);
    const newStartMinutes = newStartH * 60 + newStartM;
    const newEndMinutes = newEndH * 60 + newEndM;

    for (const entry of entries) {
      // Skip l'entrée en cours d'édition
      if (excludeId && entry.id === excludeId) continue;

      const [entryStartH, entryStartM] = entry.start.split(":").map(Number);
      const [entryEndH, entryEndM] = entry.end.split(":").map(Number);
      const entryStartMinutes = entryStartH * 60 + entryStartM;
      const entryEndMinutes = entryEndH * 60 + entryEndM;

      // Vérifier le chevauchement
      // Cas 1: La nouvelle entrée commence pendant une entrée existante
      if (newStartMinutes >= entryStartMinutes && newStartMinutes < entryEndMinutes) {
        return {
          overlaps: true,
          entry: entry
        };
      }
      // Cas 2: La nouvelle entrée se termine pendant une entrée existante
      if (newEndMinutes > entryStartMinutes && newEndMinutes <= entryEndMinutes) {
        return {
          overlaps: true,
          entry: entry
        };
      }
      // Cas 3: La nouvelle entrée englobe complètement une entrée existante
      if (newStartMinutes <= entryStartMinutes && newEndMinutes >= entryEndMinutes) {
        return {
          overlaps: true,
          entry: entry
        };
      }
    }

    return { overlaps: false };
  };

  const handleSaveEntry = async () => {
    if (!validateEntry()) return;

    //  Vérifier les chevauchements
    const excludeId = isEditing && currentEntry ? currentEntry.id : null;
    const overlapCheck = checkOverlap(startTime, endTime, excludeId);

    if (overlapCheck.overlaps) {
      setEntryMessage({ type: "error", text: `This time slot overlaps with an existing entry:\n${overlapCheck.entry.start} - ${overlapCheck.entry.end}: ${overlapCheck.entry.desc}\n\nPlease choose a different time.` });
      return;
    }

    const entryData = {
      date: formatDateForAPI(selectedDate),
      startTime,
      endTime,
      description: desc,
      workCategoryId: workCategories.find((c) => c.name === category)?.id,
      locationId: locationId,
    };

    try {
      if (isEditing && currentEntry) {
        const res = await fetch(`${API_URL}/api/tradesman/work-entries/${currentEntry.id}`, {
          method: "PUT",
          headers: authHeaders,
          body: JSON.stringify(entryData),
        });
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to update entry");
        }
        toast.success("Entry updated successfully!");
      } else {
        const res = await fetch(`${API_URL}/api/tradesman/work-entries`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify(entryData),
        });
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to create entry");
        }
        toast.success("Entry created successfully!");
      }

      // FIX PROBLÈME : Utiliser formatDateForAPI pour le fetch
      const res = await fetch(`${API_URL}/api/tradesman/work-entries?date=${formatDateForAPI(selectedDate)}`, {
        headers: authHeaders,
      });
      const data = await res.json();
      setEntries(
        data.map((e) => ({
          id: e.id,
          start: e.startTime.substring(0, 5),
          end: e.endTime.substring(0, 5),
          desc: e.description,
          category: e.workCategoryName,
          categoryId: e.workCategoryId,
          locationId: e.locationId,
          locationName: e.locationName,
        }))
          .sort((a, b) => a.start.localeCompare(b.start))
      );
      // Mémoriser les temps utilisés
      const newTimes = { startTime, endTime };
      setLastUsedTimes(newTimes);
      localStorage.setItem("lastWorkEntryTimes", JSON.stringify(newTimes));
      setShowModal(false);
    } catch (err) {
      console.error("Error saving entry:", err);
      toast.error(err.message || "Error saving entry. Please try again.");
      console.error("Error saving entry:", err);
    }
  };

  const validateEntry = () => {
    if (!startTime || !endTime || !category || !desc || !locationId) {
      setEntryMessage({ type: "error", text: "Please fill in all fields before saving" });
      return false;
    }
    const [startH, startM] = startTime.split(":").map(Number);
    const [endH, endM] = endTime.split(":").map(Number);
    const start = startH * 60 + startM;
    const end = endH * 60 + endM;

    if (end <= start) {
      setEntryMessage({ type: "error", text: "End time must be after start time." });
      return false;
    }
    const newStart = start;
    const newEnd = end;

    // Vérifie le chevauchement
    for (const e of entries) {
      if (isEditing && e.id === currentEntry?.id) continue; // ignore entry en cours d'édition

      const [eStartH, eStartM] = e.start.split(":").map(Number);
      const [eEndH, eEndM] = e.end.split(":").map(Number);
      const eStart = eStartH * 60 + eStartM;
      const eEnd = eEndH * 60 + eEndM;

      const overlap = newStart < eEnd && newEnd > eStart;
      if (overlap) {
        setEntryMessage({ type: "error", text: "The selected time range overlaps with an existing entry." });
        return false;
      }
    }

    return true;
  };

  // Auto-hide entry message (inside modal)
  useEffect(() => {
    if (!entryMessage.text) return;
    const timer = setTimeout(() => {
      setEntryMessage({ type: "", text: "" });
    }, 3000);
    return () => clearTimeout(timer);
  }, [entryMessage]);

  // Auto-hide entries area message
  useEffect(() => {
    if (!entriesMessage.text) return;
    const timer = setTimeout(() => {
      setEntriesMessage({ type: "", text: "" });
    }, 3000);
    return () => clearTimeout(timer);
  }, [entriesMessage]);

  // Auto-hide shopping list message
  useEffect(() => {
    if (!shoppingMessage.text) return;
    const timer = setTimeout(() => {
      setShoppingMessage({ type: "", text: "" });
    }, 3000);
    return () => clearTimeout(timer);
  }, [shoppingMessage]);


  useEffect(() => {
    if (showModal && !isEditing) {
      if (lastUsedTimes) {
        // Utiliser les derniers temps mémorisés
        setStartTime(lastUsedTimes.startTime);
        setEndTime(lastUsedTimes.endTime);
      } else {
        // Fallback : heure actuelle + 1h (comportement original)
        const now = new Date();
        const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
        const formatTime = (date) => date.toISOString().substring(11, 16);
        setStartTime(formatTime(now));
        setEndTime(formatTime(oneHourLater));
      }
      setCategory("");
      setLocationId("");
      setDesc("");
    }
  }, [showModal, isEditing, lastUsedTimes]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/public/work-categories/company/${storedUser.companyId}`);
        if (!response.ok) throw new Error("Failed to fetch categories");
        const data = await response.json();
        setWorkCategories(data);
      } catch (error) {
        console.error("Error fetching work categories:", error);
      }
    };

    fetchCategories();
  }, [API_URL]);

  useEffect(() => {
    const fetchEntries = async () => {
      try {

        const selectedDateStr = formatDateForAPI(selectedDate);
        const res = await authFetch(`${API_URL}/api/tradesman/work-entries?date=${selectedDateStr}`, {
          headers: authHeaders,
        });
        if (!res.ok) throw new Error("Failed to load work entries");
        const data = await res.json();
        setEntries(
          data.map((e) => ({
            id: e.id,
            start: e.startTime.substring(0, 5),
            end: e.endTime.substring(0, 5),
            desc: e.description,
            category: e.workCategoryName,
            categoryId: e.workCategoryId,
          }))
            .sort((a, b) => a.start.localeCompare(b.start))
        );
      } catch (err) {
        console.error("Error fetching entries:", err);
      }
    };

    fetchEntries();
  }, [selectedDate]);

  useEffect(() => {
    const fetchShoppingList = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/tradesman/shopping-list`, {
          headers: authHeaders,
        });
        if (res.ok) {
          const data = await res.json();
          setShoppingList(data.content || "");
        }
      } catch (err) {
        console.error("Error loading shopping list:", err);
      }
    };
    fetchShoppingList();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isShoppingListDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isShoppingListDirty]);

  useEffect(() => {
    if (!storedUser?.companyId) return;

    const fetchLocations = async () => {
      try {
        const res = await authFetch(
          `${API_URL}/api/public/locations/company/${storedUser.companyId}`,
          {
            headers: authHeaders,
          }
        );

        if (!res.ok) throw new Error("Failed to fetch locations");

        const data = await res.json();
        setLocations(data);
      } catch (err) {
        console.error("Error fetching locations:", err);
      }
    };

    fetchLocations();

  }, [API_URL, storedUser?.companyId]);



  const handleSaveShoppingList = async () => {
    try {
      // Si la liste est vide, supprimer la liste sur le serveur
      if (shoppingList.trim() === "") {
        const res = await fetch(`${API_URL}/api/tradesman/shopping-list`, {
          method: "DELETE",
          headers: authHeaders,
        });

        if (!res.ok) throw new Error("Failed to clear shopping list on server");

        setIsShoppingListDirty(false);
        setShoppingMessage({ type: "success", text: "Shopping list cleared successfully!" });
        return;
      }

      // Sinon, sauvegarder ou mettre à jour la liste
      const res = await fetch(`${API_URL}/api/tradesman/shopping-list`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ content: shoppingList }),
      });

      if (!res.ok) throw new Error("Failed to save shopping list");

      setIsShoppingListDirty(false);
      setShoppingMessage({ type: "success", text: "Shopping list updated successfully!" });
    } catch (err) {
      console.error("Error saving shopping list:", err);
      setShoppingMessage({ type: "error", text: "Error saving shopping list. Please try again." });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <div className="flex-1 max-w-full mx-auto w-full p-4 md:p-6">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-blue-900 mb-2">Welcome {firstName}</h1>
          <p className="text-gray-600">Track your working hours and manage your tasks</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={goToPrevMonth}
                className="bg-blue-900 text-white p-2 rounded hover:bg-blue-800"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <h2 className="text-xl font-bold text-blue-900">Select Date</h2>
            </div>

            <div className="flex items-center gap-2 text-gray-700">
              <Calendar className="w-5 h-5 text-yellow-500" />
              <span className="font-semibold capitalize">
                {monthName} {year}
              </span>
              <button
                onClick={goToNextMonth}
                className="bg-blue-900 text-white p-2 rounded hover:bg-blue-800"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-2">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
              <div
                key={day}
                className="text-center text-sm font-semibold text-gray-600 py-2"
              >
                {day}
              </div>
            ))}

            {daysArray.map((day, i) => {
              if (!day) {
                return <div key={`empty-${i}`} className="p-3"></div>;
              }

              const today = new Date();
              const isSelected =
                selectedDate.getDate() === day &&
                selectedDate.getMonth() === month &&
                selectedDate.getFullYear() === year;
              const isToday =
                day === today.getDate() &&
                month === today.getMonth() &&
                year === today.getFullYear();
              const isFuture = new Date(year, month, day) > today;

              return (
                <button
                  key={day}
                  onClick={() => !isFuture && setSelectedDate(new Date(year, month, day))}
                  disabled={isFuture}
                  className={`p-3 rounded-lg text-center transition-colors ${isFuture
                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                    : isSelected
                      ? 'bg-yellow-500 text-white font-bold shadow-md'
                      : isToday
                        ? 'bg-blue-100 text-blue-900 font-semibold hover:bg-yellow-100'
                        : 'bg-gray-50 text-gray-700 hover:bg-yellow-100'
                    }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-blue-900">Time Entries - {formatSelectedDate()}</h2>
              <button
                onClick={() => { setIsEditing(false); setCurrentEntry(null); setShowModal(true); }}
                className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Entry
              </button>
            </div>
            <Message type={entriesMessage.type} text={entriesMessage.text} />
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {entries.length > 0 ? (entries.map((entry) => (
                <div key={entry.id} className="border-l-4 border-yellow-500 bg-gray-50 p-4 rounded-r-lg hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold text-blue-900">{entry.start} - {entry.end}</span>
                      {(() => {
                        const matched = workCategories.find(c => c.name === entry.category);
                        const bgColor = matched ? matched.color : "#E5E7EB";
                        const textColor = "#fff";
                        return (
                          <span
                            className="ml-3 text-sm px-2 py-1 rounded font-semibold"
                            style={{ backgroundColor: bgColor, color: textColor }}
                          >
                            {entry.category}
                          </span>
                        );
                      })()}

                      {/* LOCATION */}
                      {entry.locationName && (
                        <span className="flex items-center gap-1 text-sm px-2 py-1 rounded font-semibold bg-blue-100 text-blue-800">
                          <MapPin className="w-4 h-4" />
                          {entry.locationName}
                        </span>
                      )}

                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => {
                        setIsEditing(true); setCurrentEntry(entry); setStartTime(entry.start); setEndTime(entry.end);
                        setCategory(entry.category); setDesc(entry.desc); setLocationId(entry.locationId); setShowModal(true);
                      }}
                        className="text-blue-900 hover:text-yellow-500">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ show: true, id: entry.id })}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm">{entry.desc}</p>
                </div>
              ))
              ) : (
                <div className="flex flex-col items-center justify-center h-32 text-gray-400 mt-14">
                  <Clock className="w-12 h-12 mx-auto mb-2" />
                  <p>No time entries for this date</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-blue-900 mb-4">Shopping List</h2>
            <textarea
              value={shoppingList}
              onChange={(e) => { setShoppingList(e.target.value); setIsShoppingListDirty(true); }}
              className="w-full h-64 p-4 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none text-gray-700"
              placeholder="Enter materials needed..."
            />
            <Message type={shoppingMessage.type} text={shoppingMessage.text} />
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleSaveShoppingList}
                disabled={!isShoppingListDirty}
                className={`flex-1 py-2 rounded-lg flex items-center justify-center gap-2 ${isShoppingListDirty
                  ? "bg-green-500 text-white hover:bg-green-600"
                  : "bg-green-200 text-gray-500 cursor-not-allowed"
                  }`}
              >
                <Save className="w-5 h-5" />
                Save
              </button>
              <button
                onClick={() => {
                  setShoppingList("");
                  setIsShoppingListDirty(true);
                }}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-blue-900 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="border-t-4 border-yellow-500 bg-gray-50 px-6 py-4 rounded-t-lg">
              <h3 className="text-xl font-bold text-blue-900">{isEditing ? "Edit Time Entry" : "Add Time Entry"}</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-2">Start Time</label>
                  <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-2">End Time</label>
                  <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-2">Category</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none">
                  <option value="">-- Select category --</option>
                  {workCategories.map(cat => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>

              </div>
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-2">
                  Location
                </label>

                <select
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none"
                >
                  <option value="">-- Select location --</option>

                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-2">Description</label>
                <textarea value={desc} onChange={(e) => setDesc(e.target.value)} className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none" rows="3" />
              </div>
              <Message type={entryMessage.type} text={entryMessage.text} />
              <div className="flex gap-3 pt-4">
                <button onClick={handleSaveEntry} className="flex-1 bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition-colors font-semibold">
                  {isEditing ? "Update Entry" : "Save Entry"}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-blue-900 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm transform transition-all">
            <div className="border-t-4 border-yellow-500 bg-gray-50 px-6 py-4 rounded-t-lg flex items-center justify-between">
              <h3 className="text-xl font-bold text-blue-900">Delete Entry</h3>
              <button
                onClick={() => setDeleteConfirm({ show: false, id: null })}
                className="text-gray-400 hover:text-gray-700 transition-colors p-1"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-600 mb-6 ml-4 mt-4">
              Are you sure you want to delete this entry?
            </p>
            <div className="flex flex-col gap-3 sm:flex-row-reverse m-4">
              <button
                onClick={async () => {
                  try {
                    await fetch(`${API_URL}/api/tradesman/work-entries/${deleteConfirm.id}`, {
                      method: "DELETE",
                      headers: authHeaders,
                    });
                    setEntries(entries.filter((e) => e.id !== deleteConfirm.id));
                    toast.success("Entry deleted successfully!");

                  } catch (err) {
                    console.error("Error deleting entry:", err);
                    toast.error(err.message || "Error deleting entry. Please try again.");
                  }
                  setDeleteConfirm({ show: false, id: null });
                }}
                className="flex-1 text-white font-semibold shadow-md flex items-center justify-center gap-2 bg-yellow-500 px-4 py-3 rounded-lg hover:bg-yellow-600 transition-colors"
              >
                Yes, Delete
              </button>

              <button
                onClick={() => setDeleteConfirm({ show: false, id: null })}
                className="flex-1 px-4 py-3 text-gray-700 font-semibold bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}